/**
 * API hooks for conversations
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { API_ROUTES } from "@/constants/routes";
import { useChatStore } from "@/stores/useChatStore";
import { useAuthStore } from "@/stores/useAuthStore";
import type { ConversationListItem, Conversation } from "@/types/elections";

// Query keys factory
export const conversationKeys = {
    all: ['conversations'] as const,
    lists: () => [...conversationKeys.all, 'list'] as const,
    detail: (id: number) => [...conversationKeys.all, 'detail', id] as const,
};

const fetchConversations = async (guestSessionId?: string | null): Promise<ConversationListItem[]> => {
    const params = guestSessionId ? { guest_session_id: guestSessionId } : {};
    const { data } = await api.get(API_ROUTES.conversations, { params });
    // Handle both paginated ({results: []}) and non-paginated ([]) responses
    if (Array.isArray(data)) {
        return data;
    }
    if (data && Array.isArray(data.results)) {
        return data.results;
    }
    return [];
};

const fetchConversation = async (id: number, guestSessionId?: string | null): Promise<Conversation> => {
    const params = guestSessionId ? { guest_session_id: guestSessionId } : {};
    const { data } = await api.get(`${API_ROUTES.conversations}${id}/`, { params });
    return data;
};

const createConversation = async (guestSessionId?: string | null): Promise<Conversation> => {
    const payload = guestSessionId ? { guest_session_id: guestSessionId } : {};
    const { data } = await api.post(API_ROUTES.conversations, payload);
    return data;
};

const archiveConversation = async (id: number, guestSessionId?: string | null): Promise<void> => {
    const params = guestSessionId ? { guest_session_id: guestSessionId } : {};
    await api.post(`${API_ROUTES.conversations}${id}/archive/`, params);
};

export function useConversations() {
    const { isLogged } = useAuthStore();
    const { guestSessionId } = useChatStore();

    return useQuery({
        queryKey: [...conversationKeys.lists(), isLogged ? 'user' : guestSessionId],
        queryFn: () => fetchConversations(isLogged ? null : guestSessionId),
        staleTime: 1 * 60 * 1000, // 1 minute
    });
}

export function useConversation(id: number | null) {
    const { isLogged } = useAuthStore();
    const { guestSessionId } = useChatStore();

    return useQuery({
        queryKey: conversationKeys.detail(id!),
        queryFn: () => fetchConversation(id!, isLogged ? null : guestSessionId),
        enabled: id !== null,
        staleTime: 30 * 1000, // 30 seconds
    });
}

export function useCreateConversation() {
    const queryClient = useQueryClient();
    const { isLogged } = useAuthStore();
    const { guestSessionId, initGuestSession, setCurrentConversation } = useChatStore();

    return useMutation({
        mutationFn: () => {
            const sessionId = isLogged ? null : (guestSessionId || initGuestSession());
            return createConversation(sessionId);
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
            setCurrentConversation(data.id);
        },
    });
}

export function useArchiveConversation() {
    const queryClient = useQueryClient();
    const { isLogged } = useAuthStore();
    const { currentConversationId, setCurrentConversation, guestSessionId } = useChatStore();

    return useMutation({
        mutationFn: (id: number) => archiveConversation(id, isLogged ? null : guestSessionId),
        onSuccess: (_, archivedId) => {
            queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
            if (currentConversationId === archivedId) {
                setCurrentConversation(null);
            }
        },
    });
}

export function useTransferGuestChats() {
    const queryClient = useQueryClient();
    const { guestSessionId, clearGuestSession } = useChatStore();

    return useMutation({
        mutationFn: async () => {
            if (!guestSessionId) return { conversations_transferred: 0 };
            const { data } = await api.post(API_ROUTES.transferGuestChats, {
                guest_session_id: guestSessionId,
            });
            return data;
        },
        onSuccess: () => {
            clearGuestSession();
            queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
        },
    });
}
